import {
  CircularProgress,
  Icon,
  Stack,
} from '@ace-clublabs/shared-component-library';
import { useCallback, useRef, useState } from 'react';
import SearchResultsList from '@/components/shared/SearchBox/SearchResultsList';
import { useSearchBox } from '@/components/shared/SearchBox/useSearchBox';
import SearchButton from './SearchButton';
import SearchContainer from './SearchContainer';
import SearchTextField from './SearchTextField';
import type { RSSearchBoxProps } from './types';

const RSSearchBox: React.FC<RSSearchBoxProps> = ({
  sx = [],
  placeholder,
  showWarning,
  value,
  selectedResult: controlledSelected,
  onSelect,
  onChange,
  onBlur,
  searchResults,
  isLoading = false,
  onContinueClick,
  formVariant = false,
  validator,
}) => {
  const {
    displayValue,
    selectedResult,
    handleSearchChange,
    handleSelect,
    handleClearSelected,
    handleKeyDown,
    textFieldRef,
    highlightedIndex,
  } = useSearchBox({
    value,
    selectedResult: controlledSelected,
    onSelect,
    onChange,
    searchResults,
    onConfirm: onContinueClick,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  const [openResults, setOpenResults] = useState(false);

  const handleBlur = useCallback(() => {
    onBlur?.();
    setOpenResults(false);
  }, [onBlur]);

  const handleFocus = useCallback(() => {
    setOpenResults(true);
  }, []);

  return (
    <>
      <SearchContainer
        ref={containerRef}
        onBlur={handleBlur}
        onFocus={handleFocus}
        sx={[
          ...(Array.isArray(sx) ? sx : [sx]),
          formVariant && {
            '&:focus-within': {
              borderWidth: 2,
            },
          },
          formVariant &&
            !showWarning && {
              '&:focus-within': { borderColor: 'primary.600' },
            },
          formVariant && showWarning && { borderColor: 'error.600' },
        ]}
        data-testid='rs-search-box'
      >
        <Stack
          direction='row'
          spacing={1}
          sx={[
            (theme) => ({
              mt: 1,
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 1,
              py: 0.5,
              '&:hover': {
                backgroundColor: 'primary.100',
                '& .MuiInputBase-root, & .MuiInputBase-input': {
                  backgroundColor: `${theme.palette.primary[100]} !important`,
                },
              },
            }),
            formVariant && {
              p: 0,
            },
            formVariant &&
              !!selectedResult && {
                pl: 0.5,
              },
          ]}
        >
          <SearchTextField
            ref={textFieldRef}
            placeholder={placeholder}
            placeholderColor={showWarning ? '#be3317' : undefined}
            value={displayValue}
            onChange={handleSearchChange}
            selectedResult={selectedResult}
            onClearSelected={handleClearSelected}
            onKeyDown={handleKeyDown}
            validator={formVariant ? validator : undefined}
            autofocus={!formVariant}
            startAdornment={
              showWarning ? (
                <Icon
                  iconName='triangle-exclamation-regular'
                  sx={{
                    color: '#be3317',
                    ml: 1,
                    '& path': {
                      fill: '#be3317',
                    },
                  }}
                />
              ) : !formVariant ? (
                <Icon
                  iconName='magnifying-glass-regular'
                  sx={{
                    color: 'grey.400',
                    ml: 1,
                  }}
                />
              ) : undefined
            }
            endAdornment={
              formVariant ? (
                isLoading ? (
                  <CircularProgress
                    size={20}
                    color='primary'
                    sx={{
                      display: 'flex',
                      '&.SclCircularProgress': {
                        mr: 1.5,
                      },
                    }}
                    data-testid='circularProgress'
                  />
                ) : (
                  <Icon
                    iconName='magnifying-glass-regular'
                    sx={{
                      color: 'primary.600',
                      mr: 1.5,
                    }}
                  />
                )
              ) : undefined
            }
          />
          {!formVariant && (
            <SearchButton onClick={onContinueClick} loading={isLoading} />
          )}
        </Stack>
      </SearchContainer>
      <SearchResultsList
        onSelect={handleSelect}
        open={
          ((value !== '' && !!searchResults) || !!searchResults?.length) &&
          (!formVariant || openResults)
        }
        results={searchResults}
        anchorEl={containerRef.current}
        highlightedIndex={highlightedIndex}
      />
    </>
  );
};

export default RSSearchBox;
